/**
 * 0.45.61 - Pages from the API.
 *
 * A Table or Repeating display whose rows come from an API, with its
 * paginator on, can ask the API for each page instead of receiving every
 * row in one answer and splitting it in the browser. The element says
 * how the API pages:
 *
 *   api_paging              'none' (default) | 'offset' | 'page'
 *   api_paging_offset_input input that receives the offset   ('offset')
 *   api_paging_limit_input  input that receives the page size ('limit')
 *   api_paging_page_input   input that receives the page number ('page')
 *   api_paging_size_input   input that receives the page size ('size')
 *   api_paging_page_base    0 or 1: the number of the first page (1)
 *   api_paging_total_path   where the answer gives the number of matches
 *                           ('' = unknown: Next while a page comes back full)
 *
 * The inputs are request placeholders, usually in the request path
 * (keywords={input:keywords}&offset={input:offset:number}...).
 */

export function apiPagingOf(el) {
  if (!el || el.source !== 'api' || !el.pagination_enabled) return null;
  const mode = el.api_paging;
  if (mode !== 'offset' && mode !== 'page') return null;
  const name = (v, d) => (String(v || '').trim() || d);
  return {
    mode,
    offsetInput: name(el.api_paging_offset_input, 'offset'),
    limitInput: name(el.api_paging_limit_input, 'limit'),
    pageInput: name(el.api_paging_page_input, 'page'),
    sizeInput: name(el.api_paging_size_input, 'size'),
    base: Number(el.api_paging_page_base) === 0 ? 0 : 1,
    totalPath: String(el.api_paging_total_path || '').trim(),
    perPageDefault: Number(el.per_page_default) || 10,
  };
}

/** The inputs that ask for page `page` (1-based) of `per` rows. */
export function inputsForPage(pg, page, per) {
  const p = Math.max(1, Number(page) || 1);
  const n = Math.max(1, Number(per) || pg.perPageDefault);
  if (pg.mode === 'offset') {
    return { [pg.offsetInput]: String((p - 1) * n), [pg.limitInput]: String(n) };
  }
  return { [pg.pageInput]: String(p - 1 + pg.base), [pg.sizeInput]: String(n) };
}

/** Which page (1-based) and page size a call's inputs asked for. */
export function pageOfInputs(pg, inputs) {
  const i = inputs || {};
  if (pg.mode === 'offset') {
    const per = Number(i[pg.limitInput]) || pg.perPageDefault;
    const off = Math.max(0, Number(i[pg.offsetInput]) || 0);
    return { page: Math.floor(off / per) + 1, per };
  }
  const per = Number(i[pg.sizeInput]) || pg.perPageDefault;
  const raw = i[pg.pageInput];
  const num = raw === undefined || raw === '' ? pg.base : Number(raw);
  return { page: Math.max(1, (Number.isFinite(num) ? num : pg.base) - pg.base + 1), per };
}

/**
 * Every paged element's first-page inputs, by connection key. A rule's
 * `call API` (a new search) gets them for whatever it does not set
 * itself, so a search always starts at page 1 with the element's page
 * size.
 */
export function firstPageInputsBySlug(structure) {
  const out = {};
  const walk = (els) => {
    for (const el of (els || [])) {
      if (!el || typeof el !== 'object') continue;
      const pg = apiPagingOf(el);
      const slug = (el.api_connection || '').trim();
      if (pg && slug) out[slug] = { ...(out[slug] || {}), ...inputsForPage(pg, 1, pg.perPageDefault) };
      if (Array.isArray(el.children)) walk(el.children);
      if (Array.isArray(el.columns)) {
        for (const col of el.columns) if (col && Array.isArray(col.children)) walk(col.children);
      }
    }
  };
  walk(structure);
  return out;
}
